import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Customer } from '../../../shared/types/Customer';
export function Customers() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const customers: Customer[] = [{
    id: '1',
    name: 'Acme Trading Pvt Ltd',
    email: 'billing@acme.mv',
    phone: '+960 333 4567',
    address: 'H. Maaveyo, Sosun Magu',
    city: 'Malé',
    country: 'Maldives',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  }, {
    id: '2',
    name: 'Tech Solutions Pvt Ltd',
    email: 'accounts@techsolutions.mv',
    phone: '+960 332 1234',
    address: 'M. Reefside, Boduthakurufaanu Magu',
    city: 'Hulhumalé',
    country: 'Maldives',
    createdAt: '2023-08-20',
    totalSpent: 450000,
    invoiceCount: 8,
    status: 'active'
  }, {
    id: '3',
    name: 'Atoll Enterprises Pvt Ltd',
    email: 'finance@atollent.mv',
    phone: '+960 689 7890',
    address: 'Sunshine Manzil, Link Road',
    city: 'Addu City',
    country: 'Maldives',
    createdAt: '2023-04-10',
    totalSpent: 1250000,
    invoiceCount: 15,
    status: 'active'
  }, {
    id: '4',
    name: 'Innovation Hub Pvt Ltd',
    email: 'billing@innovationhub.mv',
    phone: '+960 330 8901',
    address: 'G. Fiyaathoshige, Chandhanee Magu',
    city: 'Malé',
    country: 'Maldives',
    createdAt: '2023-09-05',
    totalSpent: 180000,
    invoiceCount: 4,
    status: 'inactive'
  }];
  const getStatusBadge = (status: Customer['status']) => {
    const styles = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-4555">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase()) || customer.phone && customer.phone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalCustomers = filteredCustomers.length;
  const activeCustomers = filteredCustomers.filter(c => c.status === 'active').length;
  const totalRevenue = filteredCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
  const avgRevenue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-4556">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-4557">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4558">
          {t('pages.Customers.customers')}
        </h1>
        <button onClick={() => navigate('/customer-form')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4559">
          {t('pages.Customers.add_customer')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4560">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4561">
          <div className="text-sm font-medium text-gray-500" data-id="element-4562">
            {t('pages.Customers.total_customers')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-4563">
            {totalCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4564">
          <div className="text-sm font-medium text-gray-500" data-id="element-4565">
            {t('pages.Customers.active_customers')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-green-600" data-id="element-4566">
            {activeCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4567">
          <div className="text-sm font-medium text-gray-500" data-id="element-4568">
            {t('pages.Customers.total_revenue')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-4569">
            {t('pages.CustomerDetail.usd')}{' '}
            {totalRevenue.toLocaleString('en-US')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4570">
          <div className="text-sm font-medium text-gray-500" data-id="element-4571">
            {t('pages.Customers.avg_revenuecustomer')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-4572">
            {t('pages.CustomerDetail.usd')}{' '}
            {Math.round(avgRevenue).toLocaleString('en-US')}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4573">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-4574">
          {/* Status Filter Tabs */}
          <div className="flex space-x-6" data-id="element-4575">
            <button onClick={() => setStatusFilter('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'all' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4576">
              {t('pages.Customers.all')}
            </button>
            <button onClick={() => setStatusFilter('active')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'active' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4577">
              {t('pages.CustomerForm.active')}
            </button>
            <button onClick={() => setStatusFilter('inactive')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'inactive' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4578">
              {t('pages.CustomerForm.inactive')}
            </button>
          </div>

          {/* Search */}
          <div className="relative w-64" data-id="element-4579">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-4580" />

            <input type="search" aria-label={t('pages.Customers.attrs.search_by_customer')} placeholder={t('pages.Customers.attrs.search_by_customer')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4581" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-4582">
          <table className="w-full text-left border-collapse" data-id="element-4583">
            <thead data-id="element-4584">
              <tr className="border-b border-gray-100" data-id="element-4585">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4586">
                  {t('pages.Customers.customer')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4587">
                  {t('pages.Customers.contact')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4588">
                  {t('pages.Customers.location')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4589">
                  {t('pages.CustomerDetail.total_spent')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4590">
                  {t('pages.CustomerDetail.invoices')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-4591">
                  {t('pages.CustomerDetail.status_2')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-4592">
              {filteredCustomers.map(customer => <tr key={customer.id} onClick={() => navigate(`/customer-detail/${customer.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-4593">
                  <td className="px-6 py-4" data-id="element-4594">
                    <div className="text-sm font-medium text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-4595">
                      {customer.name}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-4596">
                      {t('pages.Customers.since')}{' '}
                      {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4597">
                    <div className="text-sm text-gray-900" data-id="element-4598">
                      <div className="flex items-center gap-1.5 mb-1" data-id="element-4599">
                        <MailIcon size={13} className="text-gray-400" data-id="element-4600" />
                        {customer.email}
                      </div>
                      {customer.phone && <div className="flex items-center gap-1.5" data-id="element-4601">
                          <PhoneIcon size={13} className="text-gray-400" data-id="element-4602" />
                          {customer.phone}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4603">
                    <div className="text-sm text-gray-900" data-id="element-4604">{customer.city}</div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-4605">
                      {customer.country}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4606">
                    <div className="text-sm font-medium text-gray-900" data-id="element-4607">
                      {t('pages.CustomerDetail.usd')}{' '}
                      {customer.totalSpent.toLocaleString('en-US')}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4608">
                    <div className="text-sm text-gray-900" data-id="element-4609">
                      {customer.invoiceCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-4610">
                    {getStatusBadge(customer.status)}
                  </td>
                </tr>)}
              {filteredCustomers.length === 0 && <tr data-id="element-4611">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-4612">
                    {t('pages.Customers.no_customers_found_matching_your_criteria')}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}